<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import {
  TITLE_MAX_LENGTH,
  UNITS,
  VISUALIZATIONS,
  allowedMeasureTypes,
  numericFields,
  fieldRef,
} from './widgetConfig';

const props = defineProps({
  catalog: { type: Object, default: () => ({}) },
});

const title = defineModel('title', { type: String, default: '' });
const visualization = defineModel('visualization', {
  type: String,
  default: 'number',
});
const unit = defineModel('unit', { type: String, default: 'deal' });
const measureType = defineModel('measureType', {
  type: String,
  default: 'count',
});
const measureField = defineModel('measureField', { type: String, default: '' });
const dateBasis = defineModel('dateBasis', { type: String, default: 'auto' });

const { t } = useI18n();
// Clicar na opção marcada faz o ComboBox limpar a própria seleção: sem
// valor vazio, remonta o seletor para ele voltar a mostrar o valor atual.
const resetKey = ref(0);
// Recebe o NOME do campo: na template o Vue desembrulha refs de nível
// superior, então passar `unit` entregaria só o texto, não a ref.
const models = { visualization, unit, measureType, dateBasis };
const select = (name, value) => {
  if (value) models[name].value = value;
  else resetKey.value += 1;
};

const showAdvanced = ref(dateBasis.value !== 'auto');

const toOptions = (values, prefix) =>
  values.map(value => ({
    value,
    label: t(`COMMERCIAL_WIDGETS_DIALOG.${prefix}.${value.toUpperCase()}`),
  }));

const isRate = computed(() => visualization.value === 'rate');
const visualizationOptions = computed(() =>
  toOptions(VISUALIZATIONS, 'VISUALIZATIONS')
);
const unitOptions = computed(() => toOptions(UNITS, 'UNITS'));
const measureOptions = computed(() =>
  toOptions(allowedMeasureTypes(visualization.value), 'MEASURES')
);
const measureFieldOptions = computed(() =>
  numericFields(props.catalog, unit.value).map(field => ({
    value: fieldRef(field),
    label: field.name,
  }))
);
const dateBasisOptions = computed(() =>
  toOptions(
    unit.value === 'deal' ? ['auto', 'entry', 'closing'] : ['auto', 'entry'],
    'DATE_BASIS_OPTIONS'
  )
);

const labelClass = 'mb-1 text-sm font-medium text-n-slate-12';
</script>

<template>
  <div class="flex flex-col gap-4">
    <Input
      v-model="title"
      :label="t('COMMERCIAL_WIDGETS_DIALOG.FIELDS.TITLE')"
      :placeholder="t('COMMERCIAL_WIDGETS_DIALOG.FIELDS.TITLE_PLACEHOLDER')"
      :maxlength="TITLE_MAX_LENGTH"
      autofocus
    />

    <div class="flex flex-col">
      <span :class="labelClass">
        {{ t('COMMERCIAL_WIDGETS_DIALOG.FIELDS.VISUALIZATION') }}
      </span>
      <ComboBox
        :key="resetKey"
        :model-value="visualization"
        :options="visualizationOptions"
        @update:model-value="select('visualization', $event)"
      />
    </div>

    <template v-if="!isRate">
      <div class="flex flex-col">
        <span :class="labelClass">
          {{ t('COMMERCIAL_WIDGETS_DIALOG.FIELDS.UNIT') }}
        </span>
        <ComboBox
          :key="resetKey"
          :model-value="unit"
          :options="unitOptions"
          @update:model-value="select('unit', $event)"
        />
        <p class="mt-1 mb-0 text-xs text-n-slate-11">
          {{ t('COMMERCIAL_WIDGETS_DIALOG.FIELDS.UNIT_HINT') }}
        </p>
      </div>

      <div class="grid gap-4 sm:grid-cols-2">
        <div class="flex flex-col min-w-0">
          <span :class="labelClass">
            {{ t('COMMERCIAL_WIDGETS_DIALOG.FIELDS.MEASURE') }}
          </span>
          <ComboBox
            :key="resetKey"
            :model-value="measureType"
            :options="measureOptions"
            @update:model-value="select('measureType', $event)"
          />
        </div>
        <div v-if="measureType !== 'count'" class="flex flex-col min-w-0">
          <span :class="labelClass">
            {{ t('COMMERCIAL_WIDGETS_DIALOG.FIELDS.MEASURE_FIELD') }}
          </span>
          <ComboBox
            v-model="measureField"
            :options="measureFieldOptions"
            :placeholder="
              t('COMMERCIAL_WIDGETS_DIALOG.FIELDS.MEASURE_FIELD_PLACEHOLDER')
            "
            :empty-state="
              t('COMMERCIAL_WIDGETS_DIALOG.FIELDS.MEASURE_FIELD_EMPTY')
            "
          />
        </div>
      </div>

      <slot />

      <div class="flex flex-col gap-3">
        <button
          type="button"
          class="flex items-center gap-1 text-sm font-medium w-fit text-n-slate-11 hover:text-n-slate-12"
          :aria-expanded="showAdvanced"
          @click="showAdvanced = !showAdvanced"
        >
          <span
            class="i-lucide-chevron-right size-4 transition-transform duration-200 rtl:rotate-180"
            :class="{ 'rotate-90 rtl:rotate-90': showAdvanced }"
          />
          {{ t('COMMERCIAL_WIDGETS_DIALOG.ADVANCED.TITLE') }}
        </button>
        <div v-if="showAdvanced" class="flex flex-col">
          <span :class="labelClass">
            {{ t('COMMERCIAL_WIDGETS_DIALOG.ADVANCED.DATE_BASIS') }}
          </span>
          <ComboBox
            :key="resetKey"
            :model-value="dateBasis"
            :options="dateBasisOptions"
            @update:model-value="select('dateBasis', $event)"
          />
          <p class="mt-1 mb-0 text-xs text-n-slate-11">
            {{ t('COMMERCIAL_WIDGETS_DIALOG.ADVANCED.DATE_BASIS_HINT') }}
          </p>
        </div>
      </div>
    </template>
  </div>
</template>
